/**
 * CrmFeeModelEditor — edit a Fee Model's settings (grouped) + its staff rate card. Percent fields
 * display ×100 and write ÷100. Phase-proportion groups validate their basic-phase sum to 100%.
 * Fee math stays in Airtable; saving here recomputes every linked proposal.
 */
import { useEffect, useRef, useState } from 'react'
import CrmModal, { F } from './CrmModal.jsx'
import { getFeeModel, updateFeeModel, getRateCard, updateRateRow, FEE_MODEL_GROUPS } from '../../services/feeModels.js'

function round(n, d = 6) { const p = Math.pow(10, d); return Math.round(n * p) / p }
function toDisplay(field, raw) { if (raw == null || raw === '') return ''; return field.type === 'pct' ? round(raw * 100, 4) : raw }
function toStore(field, disp) { if (disp === '' || disp == null) return null; const n = parseFloat(disp); if (isNaN(n)) return null; return field.type === 'pct' ? round(n / 100, 8) : n }

export default function CrmFeeModelEditor({ modelId, onClose, onSaved }) {
  const [model, setModel] = useState(null)
  const [vals, setVals] = useState({})
  const [busy, setBusy] = useState(false)
  const [rates, setRates] = useState(null)
  const [rateEdits, setRateEdits] = useState({})
  const loadedRef = useRef(false)

  useEffect(() => {
    if (loadedRef.current) return
    loadedRef.current = true
    getFeeModel(modelId).then(m => {
      if (!m) return
      setModel(m)
      const v = {}
      for (const g of FEE_MODEL_GROUPS) for (const f of g.fields) v[f.key] = String(toDisplay(f, m.fields[f.key]) ?? '')
      setVals(v)
    })
    getRateCard(modelId).then(setRates).catch(() => setRates([]))
  }, [modelId])

  function set(key, v) { setVals(s => ({ ...s, [key]: v })) }

  async function save() {
    if (!model || busy) return
    setBusy(true)
    try {
      const patch = {}
      for (const g of FEE_MODEL_GROUPS) for (const f of g.fields) {
        const store = toStore(f, vals[f.key])
        const orig = model.fields[f.key]
        const origNum = orig == null ? null : Number(orig)
        if (store == null && origNum == null) continue
        if (store == null || origNum == null || Math.abs(store - origNum) > 1e-9) patch[f.key] = store
      }
      if (Object.keys(patch).length) {
        await updateFeeModel(modelId, patch)
        setModel(m => ({ ...m, fields: { ...m.fields, ...patch } }))
      }
      if (onSaved) onSaved()
    } finally { setBusy(false) }
  }

  async function saveRates() {
    const ids = Object.keys(rateEdits)
    if (!ids.length) return
    setBusy(true)
    try {
      for (const id of ids) await updateRateRow(id, rateEdits[id])
      setRateEdits({})
      // refetch to pull recomputed contributions
      const fresh = await getRateCard(modelId).catch(() => null)
      if (fresh) setRates(fresh)
    } finally { setBusy(false) }
  }
  function editRate(id, key, v) {
    setRateEdits(e => ({ ...e, [id]: { ...(e[id] || {}), [key]: v === '' ? null : Number(v) } }))
  }

  if (!model) return <CrmModal title="Fee model" onClose={onClose} width={780}><div style={S.muted}>Loading…</div></CrmModal>

  const footer = (
    <>
      <button style={F.btnGhost} onClick={onClose} disabled={busy}>Close</button>
      <button style={F.btnPrimary} onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save settings'}</button>
    </>
  )

  return (
    <CrmModal title={model.fields['Name'] || 'Fee model'} onClose={onClose} footer={footer} width={780}>
      {FEE_MODEL_GROUPS.map(g => {
        let sumMsg = null
        if (g.validateSum) {
          const sum = g.validateSum.reduce((s, k) => s + (toStore({ type: 'pct' }, vals[k]) || 0), 0)
          const pct = Math.round(sum * 1000) / 10
          sumMsg = { ok: Math.abs(sum - 1) < 0.0005, pct }
        }
        return (
          <div key={g.title} style={S.group}>
            <div style={S.groupHead}>
              <span style={S.groupTitle}>{g.title}</span>
              {sumMsg && <span style={{ ...S.sumBadge, color: sumMsg.ok ? '#1f9d57' : '#b3261e' }}>{sumMsg.pct}% {sumMsg.ok ? '✓' : '— must total 100%'}</span>}
            </div>
            {g.hint && <div style={S.hint}>{g.hint}</div>}
            <div style={S.grid}>
              {g.fields.map(f => (
                <div key={f.key} style={S.field}>
                  <label style={S.fl}>{f.label}{f.type === 'pct' ? ' (%)' : f.type === 'cur' ? ' ($)' : ''}</label>
                  <input style={S.input} type="number" step="any" value={vals[f.key] ?? ''} onChange={e => set(f.key, e.target.value)} />
                </div>
              ))}
            </div>
          </div>
        )
      })}

      {/* Rate card */}
      <div style={S.group}>
        <div style={S.groupHead}>
          <span style={S.groupTitle}>Staff rate card</span>
          {Object.keys(rateEdits).length > 0 && <button style={{ ...F.btnPrimary, padding: '5px 12px' }} onClick={saveRates} disabled={busy}>Save rates</button>}
        </div>
        <div style={S.hint}>Per-position billable rates used to derive the blended phase rates.</div>
        {rates === null ? <div style={S.muted}>Loading…</div>
          : rates.length === 0 ? <div style={S.muted}>No rate-card positions linked to this model.</div>
          : (
            <table style={S.table}>
              <thead><tr><th style={S.th}>Position / year</th><th style={S.thN}>Rate</th><th style={S.thN}>Contractor</th><th style={S.thN}>Target util %</th><th style={S.thN}>Contribution</th></tr></thead>
              <tbody>
                {rates.map(r => {
                  const e = rateEdits[r.id] || {}
                  return (
                    <tr key={r.id}>
                      <td style={S.td}>{r.position}</td>
                      <td style={S.tdN}><input style={S.cell} type="number" step="any" defaultValue={r.rate} onChange={ev => editRate(r.id, 'rate', ev.target.value)} /></td>
                      <td style={S.tdN}><input style={S.cell} type="number" step="any" defaultValue={r.contractorRate} onChange={ev => editRate(r.id, 'contractorRate', ev.target.value)} /></td>
                      <td style={S.tdN}><input style={S.cell} type="number" step="any" defaultValue={Math.round((r.targetUtilization || 0) * 100)} onChange={ev => editRate(r.id, 'targetUtilization', ev.target.value === '' ? '' : Number(ev.target.value) / 100)} /></td>
                      <td style={S.tdN}>{r.contribution ? '$' + Math.round(r.contribution) : '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
      </div>
    </CrmModal>
  )
}

const S = {
  group: { marginBottom: 18, borderTop: '0.5px solid var(--aj-border)', paddingTop: 14 },
  groupHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 },
  groupTitle: { fontFamily: '"Montserrat", sans-serif', fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)' },
  sumBadge: { fontFamily: '"DM Mono", monospace', fontSize: 11.5, fontWeight: 600 },
  hint: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', marginBottom: 10 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 },
  field: {},
  fl: { display: 'block', fontFamily: '"DM Sans", sans-serif', fontSize: 10.5, fontWeight: 500, color: 'var(--aj-text-mid)', marginBottom: 4 },
  input: { width: '100%', fontFamily: '"DM Mono", monospace', fontSize: 13, color: 'var(--aj-text)', padding: '6px 8px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-elevated)', boxSizing: 'border-box' },
  muted: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text-mid)', fontStyle: 'italic', padding: '4px 0' },
  table: { width: '100%', borderCollapse: 'collapse', fontFamily: '"DM Sans", sans-serif' },
  th: { textAlign: 'left', fontSize: 10.5, fontWeight: 600, color: 'var(--aj-text-mid)', padding: '4px 6px', borderBottom: '0.5px solid var(--aj-border)' },
  thN: { textAlign: 'right', fontSize: 10.5, fontWeight: 600, color: 'var(--aj-text-mid)', padding: '4px 6px', borderBottom: '0.5px solid var(--aj-border)' },
  td: { fontSize: 12.5, color: 'var(--aj-text)', padding: '5px 6px', borderBottom: '0.5px solid var(--aj-border)' },
  tdN: { textAlign: 'right', padding: '4px 6px', borderBottom: '0.5px solid var(--aj-border)' },
  cell: { width: 78, fontFamily: '"DM Mono", monospace', fontSize: 12, color: 'var(--aj-text)', padding: '4px 6px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-elevated)', textAlign: 'right' },
}
